/**
 * Standard Actions configuration for Dawn.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/* Motion Performance: supplier-aware storefront stock status */
(() => {
  const STYLE_ID = 'motion-stock-status-styles';
  const ESA_VENDOR = 'Exhaust Systems Australia';
  const BACKORDER_NOTE = "Order now and we'll dispatch once supplier stock arrives.";
  const productCache = new Map();

  function installStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .motion-stock-status {
        display: inline-flex;
        align-items: center;
        gap: .7rem;
        max-width: 100%;
        width: fit-content;
        margin-top: .8rem;
        padding: .55rem 1rem;
        border: 1px solid rgba(39, 174, 96, .28);
        border-radius: 999px;
        background: rgba(39, 174, 96, .12);
        color: #166534;
        font-size: 1.2rem;
        font-weight: 700;
        letter-spacing: .07em;
        line-height: 1.25;
        text-transform: uppercase;
        white-space: normal;
      }
      .motion-stock-status__dot {
        width: .8rem;
        height: .8rem;
        flex: 0 0 auto;
        border-radius: 50%;
        background: #22a447;
      }
      .motion-stock-status--backorder,
      .motion-stock-status--mixed {
        border-color: rgba(217, 119, 6, .34);
        background: rgba(245, 158, 11, .13);
        color: #9a5705;
      }
      .motion-stock-status--backorder .motion-stock-status__dot,
      .motion-stock-status--mixed .motion-stock-status__dot {
        background: #e28a0b;
      }
      .motion-stock-status--out {
        border-color: rgba(190, 43, 43, .28);
        background: rgba(190, 43, 43, .10);
        color: #a12626;
      }
      .motion-stock-status--out .motion-stock-status__dot {
        background: #c93636;
      }
      .motion-stock-status--checking {
        border-color: rgba(110, 110, 110, .25);
        background: rgba(110, 110, 110, .08);
        color: rgba(var(--color-foreground), .65);
      }
      .motion-stock-status--checking .motion-stock-status__dot {
        background: #8b8b8b;
      }
      .motion-stock-status--card {
        margin-top: .65rem;
        padding: .45rem .8rem;
        font-size: 1.02rem;
      }
      .motion-stock-status--product {
        margin-top: .9rem;
        margin-bottom: .35rem;
        padding: .65rem 1.1rem;
        font-size: 1.3rem;
      }
      .motion-stock-note {
        margin: .25rem 0 .6rem;
        max-width: 48rem;
        color: rgba(var(--color-foreground), .72);
        font-size: 1.25rem;
        line-height: 1.45;
      }
    `;
    document.head.appendChild(style);
  }

  function normalizeTags(tags) {
    if (Array.isArray(tags)) return tags.map((tag) => String(tag).trim());
    return String(tags || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  function supplierState(productData) {
    const tags = normalizeTags(productData?.tags);
    if (tags.includes('supplier-stock:available')) return 'available';
    if (tags.includes('supplier-stock:mixed')) return 'mixed';
    if (tags.includes('supplier-stock:out')) return 'out';
    return '';
  }

  function statusLabel(state) {
    if (state === 'in') return 'In stock';
    if (state === 'backorder') return 'Backorder – Supplier Awaiting Stock';
    if (state === 'mixed') return 'Stock Varies By Option';
    if (state === 'out') return 'Out of stock';
    return 'Checking stock';
  }

  function setStatus(status, state) {
    if (!status) return;
    status.classList.remove(
      'motion-stock-status--backorder',
      'motion-stock-status--mixed',
      'motion-stock-status--out',
      'motion-stock-status--checking'
    );
    if (state !== 'in') status.classList.add(`motion-stock-status--${state}`);
    const label = status.querySelector('[data-motion-stock-label]');
    const nextText = statusLabel(state);
    if (label && label.textContent !== nextText) label.textContent = nextText;
  }

  function makeStatus(state, extraClass) {
    const status = document.createElement('div');
    status.className = `motion-stock-status ${extraClass}`;
    status.setAttribute('data-motion-stock-status', '');
    status.setAttribute('role', 'status');
    status.innerHTML = '<span class="motion-stock-status__dot" aria-hidden="true"></span><span data-motion-stock-label></span>';
    setStatus(status, state);
    return status;
  }

  function productJsonUrl(productUrl) {
    try {
      const url = new URL(productUrl, window.location.origin);
      url.search = '';
      if (!url.pathname.endsWith('.js')) url.pathname = `${url.pathname}.js`;
      return url.toString();
    } catch {
      return null;
    }
  }

  function fetchProduct(productUrl) {
    const url = productJsonUrl(productUrl);
    if (!url) return Promise.resolve(null);
    if (productCache.has(url)) return productCache.get(url);

    const request = fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    productCache.set(url, request);
    return request;
  }

  function productUrlFromCard(card) {
    return card.querySelector('a.full-unstyled-link[href*="/products/"], a[href*="/products/"]')?.href || '';
  }

  function cardStateFromData(data) {
    if (!data) return null;
    if (data.vendor !== ESA_VENDOR) return data.available === false ? 'out' : 'in';

    const supplier = supplierState(data);
    if (data.available === false) return 'out';
    if (supplier === 'available') return 'in';
    if (supplier === 'mixed') return 'mixed';
    return 'backorder';
  }

  async function resolveCard(card) {
    const url = productUrlFromCard(card);
    if (!url) return;
    if (card.dataset.motionStockUrl === url && card.dataset.motionStockResolved === 'true') return;

    card.dataset.motionStockUrl = url;
    let status = card.querySelector('.motion-stock-status--card');
    if (!status) {
      const info = card.querySelector('.card-information');
      if (!info) return;
      status = makeStatus('checking', 'motion-stock-status--card');
      const price = info.querySelector('.price');
      if (price) price.insertAdjacentElement('afterend', status);
      else info.appendChild(status);
    } else {
      setStatus(status, 'checking');
    }

    const data = await fetchProduct(url);
    const state = cardStateFromData(data);
    if (!state || card.dataset.motionStockUrl !== url) return;
    setStatus(status, state);
    card.dataset.motionStockResolved = 'true';
  }

  function selectedVariantId(productInfo) {
    const inputs = productInfo.querySelectorAll('form[data-type="add-to-cart-form"] input[name="id"], product-form input[name="id"], input.product-variant-id');
    for (const input of inputs) {
      if (input.value) return String(input.value);
    }
    const params = new URLSearchParams(window.location.search);
    return params.get('variant') || '';
  }

  function selectedVariant(data, productInfo) {
    const id = selectedVariantId(productInfo);
    if (id) {
      const match = data?.variants?.find((variant) => String(variant.id) === String(id));
      if (match) return match;
    }
    return data?.variants?.find((variant) => variant.available) || data?.variants?.[0] || null;
  }

  function productStateFromData(data, variant) {
    if (!data || !variant) return null;
    if (data.vendor !== ESA_VENDOR) return variant.available ? 'in' : 'out';

    const supplier = supplierState(data);
    if (!variant.available) return 'out';
    if (supplier === 'available') return 'in';
    if (supplier === 'mixed') return 'in';
    return 'backorder';
  }

  function updateProductNote(productInfo, state) {
    let note = productInfo.querySelector('.motion-stock-note');
    if (state === 'backorder') {
      if (!note) {
        note = document.createElement('div');
        note.className = 'motion-stock-note';
        const status = productInfo.querySelector('.motion-stock-status--product');
        if (status) status.insertAdjacentElement('afterend', note);
      }
      if (note && note.textContent !== BACKORDER_NOTE) note.textContent = BACKORDER_NOTE;
    } else if (note) {
      note.remove();
    }
  }

  async function resolveProduct(productInfo) {
    if (!productInfo) return;
    const url = productInfo.dataset.url || window.location.pathname;
    let status = productInfo.querySelector('.motion-stock-status--product');
    if (!status) {
      const info = productInfo.querySelector('.product__info-container');
      if (!info) return;
      status = makeStatus('checking', 'motion-stock-status--product');
      const price = productInfo.querySelector('[id^="price-"]');
      if (price) price.insertAdjacentElement('afterend', status);
      else info.prepend(status);
    }

    const data = await fetchProduct(url);
    const variant = selectedVariant(data, productInfo);
    const state = productStateFromData(data, variant);
    if (!state) return;
    setStatus(status, state);
    updateProductNote(productInfo, state);
  }

  function bindProduct(productInfo) {
    if (!productInfo) return;
    resolveProduct(productInfo);
    if (productInfo.dataset.motionSupplierStockBound === 'true') return;
    productInfo.dataset.motionSupplierStockBound = 'true';

    let timer;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => resolveProduct(productInfo), 90);
    };

    productInfo.addEventListener('change', schedule);
    const observer = new MutationObserver(schedule);
    observer.observe(productInfo, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled', 'value'],
    });
  }

  function init(root = document) {
    installStyles();
    root.querySelectorAll?.('.product-card-wrapper').forEach(resolveCard);
    if (root.matches?.('.product-card-wrapper')) resolveCard(root);
    root.querySelectorAll?.('product-info').forEach(bindProduct);
    if (root.matches?.('product-info')) bindProduct(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  const pageObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('.product-card-wrapper, product-info') || node.querySelector?.('.product-card-wrapper, product-info')) {
          init(node.matches?.('.product-card-wrapper, product-info') ? node.parentElement || node : node);
        }
      }
    }
  });

  pageObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
